/**
 * Paleta do Painel resolvida em JavaScript.
 *
 * A Grade lê as cores direto das variáveis CSS. O Sunburst é canvas: o ECharts
 * não enxerga `var(--panel-attention)`, então os valores precisam ser resolvidos
 * aqui e injetados na `option`.
 *
 * Resolver **a cada leitura**, e não uma vez no import. É o defeito de
 * `DefaultChart.vue`, que fixa a cor no carregamento do módulo e por isso não
 * acompanha a troca de tema do perfil do usuário.
 */
import themeService from '@/services/themeService';
import { assetStates } from '@/domain/enums/assetState';
import { moduleStatus } from '@/domain/enums/module';

const HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Converte `#RGB` ou `#RRGGBB` em componentes.
 *
 * @param {String} value Cor hexadecimal.
 * @returns {Number[]|null} `[r, g, b]`, ou null se não for hexadecimal.
 */
/** `#0F8` e `#00FF88` descrevem a mesma cor; o segundo e o unico que se le. */
function expandShorthand(digits) {
  return digits
    .split('')
    .map((digit) => digit + digit)
    .join('');
}

export function parseHex(value) {
  const text = String(value || '').trim();

  if (!HEX_PATTERN.test(text)) {
    return null;
  }

  const digits = text.slice(1);
  const full = digits.length === 3 ? expandShorthand(digits) : digits;

  return [0, 2, 4].map((offset) => parseInt(full.substr(offset, 2), 16));
}

function toHex(channel) {
  const clamped = Math.max(0, Math.min(255, Math.round(channel)));
  return (clamped < 16 ? '0' : '') + clamped.toString(16);
}

/**
 * Interpola duas cores hexadecimais.
 *
 * Existe porque `mix()` do SASS não opera sobre variável CSS, e a Carga de
 * Atenção é uma escala contínua calculada em tempo de execução.
 *
 * @param {String} from Cor em 0.
 * @param {String} to Cor em 1.
 * @param {Number} ratio Fração entre 0 e 1.
 * @returns {String} Cor hexadecimal, ou `from` se alguma não for hexadecimal.
 */
export function mixHex(from, to, ratio) {
  const start = parseHex(from);
  const end = parseHex(to);

  if (!start || !end) {
    return from;
  }

  const amount = Math.max(0, Math.min(1, Number(ratio) || 0));

  return `#${start
    .map((channel, index) => toHex(channel + (end[index] - channel) * amount))
    .join('')}`;
}

const RGB_PATTERN = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i;

/**
 * Converte cor hexadecimal ou `rgb()`/`rgba()` em componentes e opacidade.
 *
 * Comunicação Parcial e Indeterminado chegam translúcidos das variáveis CSS
 * (60%): só hexadecimal não basta para medir a cor de um setor.
 *
 * @param {String} value Cor.
 * @returns {Object|null} `{ channels: [r, g, b], alpha }`, ou null.
 */
export function parseColor(value) {
  const hex = parseHex(value);

  if (hex) {
    return { channels: hex, alpha: 1 };
  }

  const match = RGB_PATTERN.exec(String(value || '').trim());

  if (!match) {
    return null;
  }

  return {
    channels: [match[1], match[2], match[3]].map(Number),
    alpha: match[4] === undefined ? 1 : Number(match[4]),
  };
}

export const DARK_INK = '#000000';
export const LIGHT_INK = '#FFFFFF';

/**
 * Luminância em que as duas tintas empatam, a ~4,58:1. Acima dela a escura
 * contrasta mais; abaixo, a clara. Por isso nenhuma cor fica abaixo de 4,58:1.
 */
const INK_LUMINANCE_THRESHOLD = 0.179;

/** Canal sRGB (0–255) em luminância linear, pela fórmula da WCAG. */
function channelLuminance(channel) {
  const value = channel / 255;

  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

/**
 * Tinta do texto sobre uma cor: escura sobre cor clara, clara sobre cor escura,
 * a de maior razão de contraste (WCAG).
 *
 * Razão de contraste, e não brilho YIQ como o `cell-ink` da Barra: pelo brilho,
 * o verde da Carga de Atenção zero no tema escuro levaria texto claro a 3,2:1,
 * quando o escuro dá 4,8:1. Nos tons de Status de Módulo as duas regras
 * coincidem.
 *
 * @param {String} color Cor do setor, hexadecimal ou `rgba()`.
 * @param {String} backdrop Cor por baixo, que aparece pela transparência.
 * @param {Number} [opacity] Opacidade aplicada ao setor (esmaecido pelo filtro).
 * @returns {String|null} `DARK_INK` ou `LIGHT_INK`; null se a cor não for legível.
 */
export function getInkColor(color, backdrop, opacity = 1) {
  const parsed = parseColor(color);

  if (!parsed) {
    return null;
  }

  const under = parseHex(backdrop) || [0, 0, 0];
  const alpha = Math.max(0, Math.min(1, parsed.alpha * opacity));
  const [red, green, blue] = parsed.channels
    .map((channel, index) => under[index] + (channel - under[index]) * alpha)
    .map(channelLuminance);
  const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;

  return luminance > INK_LUMINANCE_THRESHOLD ? DARK_INK : LIGHT_INK;
}

function readVar(name) {
  return String(themeService.getStyleVar(`panel-${name}`) || '').trim();
}

/**
 * Lê a paleta corrente do tema aplicado ao `body`.
 *
 * @returns {Object} Cores usadas pelo Sunburst.
 */
export default function readPanelPalette() {
  const surfaceRaised = readVar('surface-raised');
  const outOfService = readVar('out-of-service');

  return {
    background: readVar('background'),
    surface: readVar('surface'),
    surfaceRaised,
    border: readVar('border'),
    text: readVar('text'),
    textMuted: readVar('text-muted'),
    match: readVar('match'),
    attention: readVar('attention'),
    // Pontas e meio da escala da Carga de Atenção: verde, âmbar, vermelho.
    normal: readVar('normal'),
    loadMid: readVar('load-mid'),
    states: {
      [assetStates.ATTENTION]: readVar('attention'),
      [assetStates.NORMAL]: readVar('normal'),
      [assetStates.OUT_OF_SERVICE]: outOfService,
    },
    /**
     * Cores de Status de Módulo, as mesmas que a Grade usa nas células de
     * Sensor. O anel de Sensor fala o vocabulário de oito valores da listagem,
     * e não os três do Estado do Ativo.
     */
    sensors: {
      [moduleStatus.ACTIVE]: readVar('sensor-active'),
      [moduleStatus.INACTIVE]: readVar('sensor-inactive'),
      [moduleStatus.PARTIAL_COMMUNICATION]: readVar(
        'sensor-partial-communication',
      ),
      [moduleStatus.ESTABLISHING_COMMUNICATION]: readVar(
        'sensor-establishing-communication',
      ),
      [moduleStatus.INDETERMINATE]: readVar('sensor-indeterminate'),
      [moduleStatus.DISABLED]: readVar('sensor-disabled'),
      [moduleStatus.UNDER_MAINTENANCE]: readVar('sensor-under-maintenance'),
      [moduleStatus.SIMULATION]: readVar('sensor-simulation'),
    },
    /**
     * Nó sem nenhum Ativo não-neutro: a Carga de Atenção não existe, e pintá-lo
     * de 0% afirmaria saúde onde não há operação. Recebe um tom próprio,
     * puxado para o cinza de Fora de Operação.
     */
    loadEmpty: mixHex(surfaceRaised, outOfService, 0.45),
  };
}
